/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import { useMemo } from "react";
import { BOOKING_STATUS, INVOICE_STATUS } from "../constants/constants";
import usePluginUtils from "./usePluginUtils";

export const useBookingStatus = ({ bookingData = null, invoiceData = null, initialStatus = null, source = null }) => {
  const { isPluginEnabled } = usePluginUtils();

  const computedStatus = useMemo(() => {
    // Priority order for status determination:
    // 1. Initial status if it's a final state (e.g., CANCELLED from route navigation)
    // 2. Invoice-based status with business logic
    // 3. Direct booking status
    // 4. Fallback to initial status

    // If initial status is a final state (like CANCELLED), prioritize it
    // This handles cases like navigating to cancelled route after cancellation
    const finalStatuses = [BOOKING_STATUS.CANCELLED, BOOKING_STATUS.ERROR];

    if (initialStatus && finalStatuses.includes(initialStatus)) {
      return initialStatus;
    }

    const directBookingStatus = bookingData?.status;

    const invoiceStatus = invoiceData?.status;
    const paymentProcessor = invoiceData?.payment_processor;
    const paymentReceived = invoiceData?.payment_received;

    if (invoiceData) {
      if (paymentProcessor === "delay" && !paymentReceived) {
        if (isPluginEnabled("approve_booking")) {
          return BOOKING_STATUS.PENDING_DELAY;
        }

        return BOOKING_STATUS.DELAY;
      }

      if (invoiceStatus === INVOICE_STATUS.PAID) {
        if (paymentReceived) {
          return BOOKING_STATUS.CONFIRMED;
        }

        if (isPluginEnabled("approve_booking")) {
          return BOOKING_STATUS.PENDING;
        }
        return BOOKING_STATUS.CONFIRMED;
      }

      switch (invoiceStatus) {
        case INVOICE_STATUS.PENDING:
        case INVOICE_STATUS.NEW:
          return BOOKING_STATUS.PENDING_PAYMENT;
        case INVOICE_STATUS.ERROR:
          return BOOKING_STATUS.ERROR;
        case INVOICE_STATUS.CANCELLED:
        case INVOICE_STATUS.CANCELLED_BY_TIMEOUT:
          return BOOKING_STATUS.CANCELLED;
        default:
          break;
      }
    }

    if (directBookingStatus) {
      return directBookingStatus;
    }

    return initialStatus || BOOKING_STATUS.PENDING;
  }, [bookingData, invoiceData, initialStatus, source, isPluginEnabled]);

  const statusMetadata = useMemo(() => {
    const metadata = {
      [BOOKING_STATUS.CONFIRMED]: {
        allowsCancellation: true,
        showBookingDetails: true,
        showAddToCalendar: true,
        showDateLabels: true,
        showOnlineMeetingLink: true,
      },
      [BOOKING_STATUS.PENDING]: {
        allowsCancellation: true,
        showBookingDetails: false,
        showAddToCalendar: false,
        showDateLabels: true,
        showOnlineMeetingLink: false,
      },
      [BOOKING_STATUS.PENDING_DELAY]: {
        allowsCancellation: false,
        showBookingDetails: true,
        showAddToCalendar: false,
        showDateLabels: true,
        showOnlineMeetingLink: false,
      },
      [BOOKING_STATUS.PENDING_PAYMENT]: {
        allowsCancellation: true,
        showBookingDetails: false,
        showAddToCalendar: false,
        showDateLabels: true,
        showOnlineMeetingLink: false,
      },
      [BOOKING_STATUS.DELAY]: {
        allowsCancellation: true,
        showBookingDetails: true,
        showAddToCalendar: true,
        showDateLabels: true,
        showOnlineMeetingLink: false,
      },
      [BOOKING_STATUS.CANCELLED]: {
        allowsCancellation: false,
        showBookingDetails: false,
        showAddToCalendar: false,
        showDateLabels: false,
        showOnlineMeetingLink: false,
      },
      [BOOKING_STATUS.ERROR]: {
        allowsCancellation: false,
        showBookingDetails: false,
        showAddToCalendar: false,
        showDateLabels: false,
        showOnlineMeetingLink: false,
      },
    };

    return metadata[computedStatus] || metadata[BOOKING_STATUS.PENDING];
  }, [computedStatus]);

  return {
    status: computedStatus,
    metadata: statusMetadata,
    isConfirmed: computedStatus === BOOKING_STATUS.CONFIRMED,
    isPending:
      computedStatus === BOOKING_STATUS.PENDING ||
      computedStatus === BOOKING_STATUS.PENDING_DELAY ||
      computedStatus === BOOKING_STATUS.PENDING_PAYMENT,
    isCancelled: computedStatus === BOOKING_STATUS.CANCELLED,
    isError: computedStatus === BOOKING_STATUS.ERROR,
    isDelay: computedStatus === BOOKING_STATUS.DELAY || computedStatus === BOOKING_STATUS.PENDING_DELAY,
    canBeCancelled: statusMetadata.allowsCancellation,
    shouldShowBookingDetails: statusMetadata.showBookingDetails,
    shouldShowAddToCalendar: statusMetadata.showAddToCalendar,
    shouldShowDateLabels: statusMetadata.showDateLabels,
    shouldShowOnlineMeetingLink: statusMetadata.showOnlineMeetingLink,
  };
};

export default useBookingStatus;
